import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";

import EntityComponent from "../../../components/common/entity-component";
import Snackbar from "../../../components/common/Snackbar";

import config from "./config";
import RoundtableForm from "./form";
import Store from "../../../types/Store";
import StoreUser from "../../../types/StoreUser";

import getAllRoundtables from "../../../database/roundtables/getAllRoundtables";
import createRoundtable from "../../../database/roundtables/createRoundtable";
import editRoundtable from "../../../database/roundtables/editRoundtable";
import removeRoundtable from "../../../database/roundtables/removeRoundtable";
import { RoundtableFormValues } from "../../../database/roundtables/mapRoundtableForm";

type Props = {
  store: Store;
  storeUser: StoreUser;
};

// Roundtables content only. Page chrome (surface, header, sub-tabs) lives in the
// events/index container so this sits under the Events nav item as a sub-tab.
const RoundtablesTab = (props: Props) => {
  const { storeUser } = props;
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    error: boolean;
  }>({ open: false, message: "", error: false });

  const response = getAllRoundtables({ communityId: storeUser?.communityId });

  useEffect(() => {
    if (response.error) {
      setSnackbar({ open: true, message: "Could not load roundtables.", error: true });
    }
  }, [response.error]);

  const creatorName =
    storeUser?.fullName ||
    [storeUser?.firstName, storeUser?.lastName].filter(Boolean).join(" ") ||
    undefined;

  const onCreateRoundtable = async (values: RoundtableFormValues) => {
    if (!storeUser?.communityId || !storeUser?.id) {
      setSnackbar({
        open: true,
        message: "Missing community or admin identity.",
        error: true,
      });
      return;
    }

    setSnackbar({
      open: true,
      message: "Generating the AI agenda and publishing the roundtable...",
      error: false,
    });

    try {
      await createRoundtable({
        values,
        communityId: storeUser.communityId,
        createdBy: storeUser.id,
        creatorName,
      });
      setSnackbar({ open: true, message: "Roundtable published.", error: false });
    } catch (error: any) {
      console.error("Error creating roundtable:", error);
      setSnackbar({
        open: true,
        message: error?.message || "Could not create the roundtable.",
        error: true,
      });
    }
  };

  const onEditRoundtable = (values: RoundtableFormValues & { id: string }) => {
    editRoundtable({ values });
  };

  const onRemoveRoundtable = ({ id }: { id: string }) => {
    removeRoundtable({ id });
  };

  return (
    <Fragment>
      <EntityComponent
        canBeCreated
        canBeRemoved
        canBeEdited
        onRemoveItem={onRemoveRoundtable}
        onEditItem={onEditRoundtable}
        onCreateItem={onCreateRoundtable}
        singularEntityName="Roundtable"
        pluralEntityName="Roundtables"
        filterConfig={config.filterConfig}
        tabConfig={config.tabConfig}
        dataset={response.roundtables}
        Form={(formProps: any) => <RoundtableForm {...formProps} />}
      />
      <Snackbar
        onCloseSnackbar={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
        isOpen={snackbar.open}
        message={snackbar.message}
        error={snackbar.error}
      />
    </Fragment>
  );
};

const mapStateToProps = (state: any) => ({
  store: state.store,
  storeUser: state.storeUser,
});

export default connect(mapStateToProps, null)(RoundtablesTab);
